import React, { useState, useEffect, useRef, Component } from "react";
import { BrowserRouter, Routes, Route, matchPath, useLocation } from 'react-router-dom';
import Login from './pages/login';
import Search from './pages/search';

import StoryAdd from './pages/story/StoryAdd';
import StoryStatistic from './pages/story/StoryStatistic';
import StoryUsers from './pages/story/StoryUsers';
import StoryFeedEntry from './pages/story/StoryFeedEntry';
import ReactionLine from './pages/reaction/ReactionLine';
import ReactionEditor from './pages/reaction/ReactionEditor';
import ReactionReport from './pages/reaction/ReactionReport';
import UserProfile from './pages/user/UserProfile';
import NotificationsProvider from './notifications/NotificationsProvider';
import InAppProvider from './inApp/InAppProvider';
import NotificationsPage from './notifications/NotificationsPage';
import UserSettings from './pages/user/UserSettings';
import UserFollower from './pages/user/UserFollower';
import UserControl from './pages/user/UserControl';
import Page404 from './pages/404';
import BrandLoader from './components/Brand/BrandLoader';
import AppFailure from './components/Brand/AppFailure';
import MessageLayout from './components/Brand/MessageLayout';
import { FooterAvatarProvider } from './components/Buttons/ButtonLoginOrProfile/FooterAvatar';
import Page403 from './pages/403';
import Static from './pages/static';
import AppUrlListener from './components/AppUrlListener';
import AppInstallBanner from './components/AppInstallBanner';
import { readRegistrationStatus } from './legal/registration';
import LanguageDialog from './onboarding/LanguageDialog';
import { onboardingLanguageChosen, confirmOnboardingLanguage } from './onboarding/state';
import { readPreferredLanguage } from './utils/languagePreference';
import OnboardingProvider, { useOnboarding } from './onboarding/OnboardingProvider';
import { prepareOnboardingLaunch } from './onboarding/launch';
import { useTranslation } from "react-i18next";
import config from './utils/config';
import { setLocalStorage, getLocalStorage, removeUser } from './utils/func';
import { APP_ROUTES } from './utils/constant';
import api from './services';
import {Helmet} from "react-helmet";
import { ConfigProvider } from 'antd';
import  varCss from './variable.scss';
import { useViewportCss } from './hooks/useViewport';
import useActionFocus from './hooks/useActionFocus';
import useSessionIdentity from './hooks/useSessionIdentity';
import { initialization as translationsReady } from './i18n';
import changeLanguageInOrder from './utils/changeLanguageInOrder';
import LegalConsentProvider from './legal/LegalConsentProvider';

const { userByToken } = api;
const uiRadius = parseFloat(varCss['ui-radius']) || 4;

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('WIW render error:', error, info); }
  render() { return this.state.error ? <AppFailure /> : this.props.children; }
}

function AppContent() {
  useViewportCss();
  useActionFocus();
  const session = useSessionIdentity();
  const { ready: entryReady, eligible: onboardingEligible, scope: onboardingScope } = useOnboarding();
  const [, refreshLanguageGate] = useState(0);
  const languageNeeded = onboardingEligible && !onboardingLanguageChosen(onboardingScope);
  const location = useLocation();
  // Readiness participates in rendering. A ref can leave the cold-login
  // destination stuck on its loader if React batches loading true → false.
  const [initialized, setInitialized] = useState(false);
  const initializationFinished = useRef(false);
  const loginToken = useRef(undefined);
  const token = getLocalStorage(config.token);
  const authenticatedFromLogin = loginToken.current !== undefined && Boolean(token) && token !== loginToken.current;
  const isLoginRoute = config.languages.some(language =>
    matchPath(`${language ? `/${language}` : ''}${APP_ROUTES.LOGIN}`, location.pathname));
  const isLegalRoute = config.languages.some(language =>
    matchPath(`${language ? `/${language}` : ''}${APP_ROUTES.STATIC}/:url`, location.pathname));
  const [load, setLoad] = useState(true);
  const [registration, setRegistration] = useState(null);
  const registrationRequired = registration?.token === token && registration?.required === true;
  const [initError, setInitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // Brand strings are bundled. Do not suspend this component behind the
  // remote translation file: session validation can start at the same time.
  const { t, i18n } = useTranslation('brand', { useSuspense: false });


  useEffect(() => {
    if (initialized || initializationFinished.current || !entryReady) return;
    // finishSignIn has already validated and installed this complete session
    // and its language. Skip/Back with the old token still needs validation.
    // installSession publishes synchronously before finishSignIn navigates.
    // Recognize it while still on /login too: restarting there would replace
    // the original token baseline and reset the completed account language.
    if (authenticatedFromLogin) {
      initializationFinished.current = true;
      setInitialized(true);
      setLoad(false);
      setInitError(null);
      return;
    }
    if (isLoginRoute && loginToken.current === undefined) loginToken.current = getLocalStorage(config.token);
    let cancelled = false;
    const requestToken = getLocalStorage(config.token);
    const controller = new AbortController();
    const currentSession = () => !cancelled && getLocalStorage(config.token) === requestToken;
    const restartChangedSession = () => {
      if (cancelled || currentSession()) return false;
      setRetryCount(value => value + 1);
      return true;
    };
    const languageInPath = window.location.pathname.split('/')[1];
    const initialLanguage = languageInPath && config.languages.includes(languageInPath) ? languageInPath : readPreferredLanguage() || 'en';
    const initialize = async () => {
      setLoad(true);
      setInitError(null);
      try {
        const [, response] = await Promise.all([
          translationsReady.then(() => changeLanguageInOrder(i18n, initialLanguage, currentSession)),
          // An OAuth return must reach its handler even if the old session
          // cannot be checked. The login handler installs the new session.
          isLoginRoute ? Promise.resolve(null) : userByToken(undefined, { signal: controller.signal }),
        ]);
        if (cancelled || restartChangedSession()) return;
        if (!isLoginRoute && !response?.success) throw new Error('User initialization unavailable');
        if (response?.user) {
          setLocalStorage(config.user, JSON.stringify(response.user));
          const required = requestToken ? await readRegistrationStatus(requestToken, controller.signal) : false;
          if (cancelled || restartChangedSession()) return;
          setRegistration({ token: requestToken, required });
          const preferredLanguage = response.user.lng_code;
          if (preferredLanguage !== initialLanguage &&
            (preferredLanguage === 'en' || config.languages.includes(preferredLanguage))) {
            await changeLanguageInOrder(i18n, preferredLanguage || 'en', currentSession);
          }
        } else if (!isLoginRoute) {
          // useSyncExternalStore can render before these batched state updates
          // when removeUser publishes the guest session. Mark completion
          // synchronously too, preventing a second, orphaned initialization.
          initializationFinished.current = true;
          setRegistration(null);
          setInitialized(true);
          setLoad(false);
          // A native cold OAuth launch may arrive after the bridge fallback.
          // Passive session validation is not a deliberate logout; keep the
          // short-lived, state-bound attempt for its pending return handler.
          removeUser({ preserveAuthAttempts: true });
          return;
        }
        if (!cancelled && !restartChangedSession() && !isLoginRoute) {
          initializationFinished.current = true;
          setInitialized(true);
        }
      } catch (error) {
        if (!cancelled && !restartChangedSession()) { console.error('App init error:', error); setInitError(error); }
      } finally {
        if (!cancelled) setLoad(false);
      }
    };
    initialize();
    return () => { cancelled = true; controller.abort(); };
  }, [i18n, retryCount, isLoginRoute, authenticatedFromLogin, initialized, token, entryReady]);

  const retry = () => {
    setLoad(true);
    setInitError(null);
    setRetryCount(value => value + 1);
  };

  const routesItems = config.languages.map((l) =>
      <React.Fragment key={l}>
          <Route path={`${l}${APP_ROUTES.MAIN}`} element={<ReactionLine/> } />
          <Route path={`${l}/unsubscribe`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.SEARCH}`} element={<Search />} />
          <Route path={`${l}/notifications`} element={<NotificationsPage />} />
          <Route path={`${l}${APP_ROUTES.LOGIN}`} element={<Login />} />
          <Route path={`${l}${APP_ROUTES.POST}/:storyId/statistic`} element={<StoryStatistic />} />
          <Route path={`${l}${APP_ROUTES.POST}/:storyId/reactions`} element={<StoryUsers />} />
          <Route path={`${l}${APP_ROUTES.POST}/:storyId/feed`} element={<StoryFeedEntry />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/:id`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.REACTION}/:id`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/:id/report`} element={<ReactionReport />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/opinion/:reactionId`} element={<ReactionEditor />} />
          <Route path={`${l}${APP_ROUTES.POST}/:id${APP_ROUTES.REACTION_EDIT}`} element={<ReactionEditor />} />
          <Route path={`${l}${APP_ROUTES.STORY_ADD}`} element={<StoryAdd />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username`} element={<UserProfile />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.USER_FOLLOWERS}`} element={<UserFollower type="followers" />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.USER_FOLLOWING}`} element={<UserFollower type="following" />} />
          <Route path={`${l}${APP_ROUTES.USER_SETTINGS}`} element={<UserSettings />} />
          <Route path={`${l}${APP_ROUTES.USER_CONTROL}`} element={<UserControl />} />
          <Route path={`${l}${APP_ROUTES.PAGE404}`} element={<Page404 />} />
          <Route path={`${l}${APP_ROUTES.PAGE403}`} element={<Page403 />} />
          <Route path={`${l}${APP_ROUTES.STATIC}/:url`} element={<Static />} />
      </React.Fragment>
  );

  return (
    <ConfigProvider
      wave={{ disabled: true }}
      theme={{
        token: {
          fontFamily: "'Source Sans Pro', system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: 18,
          colorBgBase: varCss.surface,
          colorBgContainer: varCss.surface,
          colorBgElevated: varCss.surface,
          colorBgLayout: varCss.surface,
          colorWhite: varCss.surface,
          colorTextLightSolid: varCss.surface,
          // Pointer feedback must not paint transient grey/blue fills. Actual
          // selected states retain their own component/business tokens.
          colorBgTextHover: 'transparent',
          colorBgTextActive: 'transparent',
          controlItemBgHover: 'transparent',
          controlHeight: 48,
          controlHeightLG: 48,
          lineHeight: 4 / 3,
          activeBorderColor: varCss['color-black'],
          activeShadow: 'none',
          hoverBorderColor: varCss['color-black'],
          colorPrimary: varCss['color-primary'],
          borderRadius: uiRadius,
          borderRadiusLG: uiRadius,
          borderRadiusSM: uiRadius,
          borderRadiusXS: uiRadius,
          lineWidth: 2,
          colorBorder: varCss['color-border'],
          colorPrimaryActive: varCss['color-primary']
        },
        components: {
          Input: {
            hoverBorderColor: varCss['color-border'],
            activeBorderColor: varCss['color-border'],
            activeShadow: 'none',
          },
          Radio: {
            colorPrimaryBorder: varCss['color-border'],
            buttonSolidCheckedBg: varCss['color-border']
          },
          Select: {
            singleItemHeightLG: 'auto',
            optionActiveBg: 'transparent',
          },
          Button: {
            onlyIconSize: '24px',
            defaultColor: varCss['color-primary'],
            defaultHoverBg: varCss.surface,
            defaultActiveBg: varCss.surface,
            defaultActiveBorderColor: varCss['color-border'],
            defaultGhostBorderColor: varCss['color-border'],
            ghostBg: 'transparent',
            textHoverBg: 'transparent',
            colorBgTextActive: 'transparent',
            colorPrimaryHover: varCss['color-primary'],
            colorPrimaryActive: varCss['color-primary'],
            colorPrimaryTextHover: 'green',
            colorPrimaryBorder: 'red',
            colorPrimaryBorderHover: varCss['color-border'],
            controlOutline: 'transparent',
            controlOutlineWidth: 0
          }
        }
      }}
    >
      <>
        <MessageLayout />
        <LegalConsentProvider />
        <AppUrlListener />
        <AppInstallBanner suppressed={!entryReady || onboardingEligible} />
        <Helmet>
          <html lang={i18n.resolvedLanguage || i18n.language || 'en'} />
          <title>WIW</title>
          <meta name="description" content="Who is Who" />
        </Helmet>
        <NotificationsProvider ready={!load && !initError && !isLoginRoute && !registrationRequired && !languageNeeded && (initialized || authenticatedFromLogin)}>
        <InAppProvider ready={!load && !initError && !isLoginRoute && !registrationRequired && !languageNeeded && (initialized || authenticatedFromLogin)}>
        {/* A credential change resets private screens and failed avatar loads;
            ordinary navigation/profile edits retain the decoded footer photo. */}
        <FooterAvatarProvider key={session}>
        {!entryReady || load || (!isLoginRoute && !initialized && !authenticatedFromLogin && !initError)
          ? <BrandLoader fullPage label={t('loading')} /> : initError ?
          <AppFailure onRetry={retry} recoverOnResume /> : registrationRequired && !isLoginRoute && !isLegalRoute ?
          <Login confirmSession onConsentSaved={() => setRegistration(null)} onSkip={() => {
            removeUser(); setRegistration(null);
          }} /> : languageNeeded ?
          <LanguageDialog key={onboardingScope} onContinue={() => {
            confirmOnboardingLanguage(onboardingScope); refreshLanguageGate(value => value + 1);
          }} /> :
          <Routes>{routesItems}<Route path="*" element={<Page404 />} /></Routes>}
        </FooterAvatarProvider>
        </InAppProvider>
        </NotificationsProvider>
      </>
    </ConfigProvider>
  );
}

export default function App() {
  const [appScope] = useState(prepareOnboardingLaunch);
  return <BrowserRouter><ErrorBoundary><OnboardingProvider appScope={appScope}><AppContent /></OnboardingProvider></ErrorBoundary></BrowserRouter>;
}
